import {
  type CollectionRequirementSettings,
  getSettingForComponentType,
  isComponentAllowedOnPage,
  isComponentTypeAllowed,
  pickT,
} from "@repo/shared";
import {
  RISK_SECTIONS,
  type RiskSection,
  type SumsubMode,
  type TrapetsMode,
} from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  allowedPageTypesFor,
  type FlowComponentTypeInfo,
  getFlowComponentCategories,
  getFlowComponentTypes,
} from "@/components/flow/flow-component-types";
import { SearchInput } from "@/components/ui/search-input";
import type { ComponentType, PageType } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { getComponentIcon } from "../../../shared/component-icons";
import { T } from "./component-selector.i18n";

/** QL-212 — the widget's four sections, as translation keys. */
const RISK_SECTION_KEYS = {
  timeHorizon: "riskSectionTimeHorizon",
  riskLevel: "riskSectionRiskLevel",
  adjustmentQuestions: "riskSectionAdjustmentQuestions",
  result: "riskSectionResult",
} as const satisfies Record<RiskSection, keyof typeof T.en_GB>;

type ComponentSelectorProps = {
  flowLanguage: string;
  pageType: PageType;
  /**
   * QL-314 — the page's visibility. Advisor-only components (volatility_chart)
   * are hidden on public pages; the server-side guard refuses them either way.
   */
  pageVisibility?: "internal" | "public";
  hasXRayAccess: boolean;
  hasRiskModel: boolean;
  hasPortfolioCollection: boolean;
  hasFinancialSituationModel: boolean;
  collectionSettings?: CollectionRequirementSettings | null;
  trapetsMode?: TrapetsMode | null;
  sumsubMode?: SumsubMode | null;
  /**
   * QL-212 — the sections a risk-assessment placement would render on THIS page
   * for this flow's risk model, or `null` while the model is unknown (still
   * loading, or the flow has none). An empty set means the placement would show
   * nothing, which is exactly what the server-side guard refuses.
   */
  riskSections?: ReadonlySet<RiskSection> | null;
  selectedType: ComponentType;
  onSelect: (type: ComponentType) => void;
};

export function ComponentSelector({
  flowLanguage,
  pageType,
  pageVisibility,
  hasXRayAccess,
  hasRiskModel,
  hasPortfolioCollection,
  hasFinancialSituationModel,
  collectionSettings,
  trapetsMode,
  sumsubMode,
  riskSections = null,
  selectedType,
  onSelect,
}: ComponentSelectorProps) {
  const t = pickT(T, flowLanguage);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(["input"]));

  // A requirement-governed component is shown but blocked when its governing collection
  // setting is off. The server-side guard rejects assignment either way; this only drives
  // the picker's greyed-out + disabled-Add presentation.
  const isBlockedByCollectionSettings = (type: ComponentType): boolean =>
    !!collectionSettings && !isComponentTypeAllowed(collectionSettings, type);

  // PEP/Sanction Lookup is blocked when the tenant's Trapets mode is disabled (same
  // shown-but-greyed presentation; the disable guard from settings keeps definitions clean).
  const isBlockedByTrapetsDisabled = (type: ComponentType): boolean =>
    type === "trapets_lookup" && trapetsMode === "disabled";

  // Sumsub Verification is blocked when the tenant's Sumsub mode is disabled (same
  // shown-but-greyed presentation; the disable guard from settings keeps definitions clean).
  const isBlockedBySumsubDisabled = (type: ComponentType): boolean =>
    type === "sumsub_verification" && sumsubMode === "disabled";

  // QL-212 — the risk assessment is legal on global and goal pages, but the
  // model's scopes decide which of its sections show on each. A placement whose
  // rendered set is empty draws an empty box, so the picker blocks it here the
  // same way the service refuses it on save.
  const isBlockedByEmptyRiskSections = (type: ComponentType): boolean =>
    type === "risk_assessment" && riskSections?.size === 0;

  const isBlocked = (type: ComponentType): boolean =>
    isBlockedByCollectionSettings(type) ||
    isBlockedByTrapetsDisabled(type) ||
    isBlockedBySumsubDisabled(type) ||
    isBlockedByEmptyRiskSections(type);

  // Filter available component types based on page type and feature flags
  const availableComponentTypes = useMemo(() => {
    return getFlowComponentTypes(flowLanguage).filter((ct) => {
      // Page-type placement — authoritative shared policy (QP-150), same matrix the
      // server-side validation enforces.
      if (pageType && !isComponentAllowedOnPage(ct.type, pageType, pageVisibility)) return false;

      // Feature flag restrictions
      if (ct.requiresXRay && !hasXRayAccess) return false;
      if (ct.requiresRiskModel && !hasRiskModel) return false;
      if (ct.requiresPortfolioCollection && !hasPortfolioCollection) return false;
      if (ct.requiresFinancialSituationModel && !hasFinancialSituationModel) return false;

      return true;
    });
  }, [
    pageType,
    hasXRayAccess,
    hasRiskModel,
    hasPortfolioCollection,
    hasFinancialSituationModel,
    flowLanguage,
    pageVisibility,
  ]);

  // Group components by category
  const componentsByCategory = useMemo(() => {
    const groups: Record<string, FlowComponentTypeInfo[]> = {};
    for (const ct of availableComponentTypes) {
      if (!groups[ct.category]) {
        groups[ct.category] = [];
      }
      groups[ct.category].push(ct);
    }
    return groups;
  }, [availableComponentTypes]);

  // Filter by search query
  const filteredComponentsByCategory = useMemo(() => {
    if (!searchQuery.trim()) return componentsByCategory;

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, FlowComponentTypeInfo[]> = {};

    for (const [category, components] of Object.entries(componentsByCategory)) {
      const matchingComponents = components.filter(
        (ct) =>
          ct.label.toLowerCase().includes(query) ||
          ct.description.toLowerCase().includes(query) ||
          ct.detailedDescription.toLowerCase().includes(query),
      );
      if (matchingComponents.length > 0) {
        filtered[category] = matchingComponents;
      }
    }

    return filtered;
  }, [componentsByCategory, searchQuery]);

  // Get selected component info
  const selectedComponentInfo = useMemo(() => {
    return availableComponentTypes.find((ct) => ct.type === selectedType);
  }, [availableComponentTypes, selectedType]);

  const SelectedIcon = selectedComponentInfo ? getComponentIcon(selectedComponentInfo.type) : null;

  const selectedBlocked = selectedComponentInfo ? isBlocked(selectedComponentInfo.type) : false;
  const selectedBlockedReason =
    selectedComponentInfo && selectedBlocked
      ? isBlockedByEmptyRiskSections(selectedComponentInfo.type)
        ? t.riskShowsNothing
        : isBlockedByTrapetsDisabled(selectedComponentInfo.type)
          ? t.trapetsDisabledBlock
          : isBlockedBySumsubDisabled(selectedComponentInfo.type)
            ? t.sumsubDisabledBlock
            : getSettingForComponentType(selectedComponentInfo.type) === "requiresTimeseries"
              ? t.requiresTimeseriesSetting
              : t.requiresAssetAllocationSetting
      : null;

  // Auto-expand all categories when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedCategories(new Set(Object.keys(filteredComponentsByCategory)));
    }
  }, [searchQuery, filteredComponentsByCategory]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      if (prev.has(categoryId)) {
        // Closing this category
        return new Set();
      }
      // Opening this category - also select first component
      const firstInCategory = filteredComponentsByCategory[categoryId]?.[0];
      if (firstInCategory) {
        onSelect(firstInCategory.type);
      }
      return new Set([categoryId]);
    });
  };

  return (
    <div className="grid grid-cols-[220px_1fr] gap-4 min-h-[400px]">
      {/* Left: Search and component list */}
      <div className="border rounded-lg overflow-hidden flex flex-col">
        {/* Search input */}
        <div className="p-2 border-b">
          <SearchInput
            data-dialog-guard-ignore
            placeholder={t.searchComponents}
            value={searchQuery}
            onValueChange={setSearchQuery}
            inputClassName="h-8 text-sm"
          />
        </div>
        {/* Component list grouped by collapsible categories */}
        <div className="flex-1 overflow-y-auto max-h-[350px]">
          {Object.keys(filteredComponentsByCategory).length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              {t.noComponentsFound}
            </div>
          ) : (
            getFlowComponentCategories(flowLanguage).map((category) => {
              const components = filteredComponentsByCategory[category.id];
              if (!components || components.length === 0) return null;

              const isSearching = searchQuery.trim().length > 0;
              const isExpanded = isSearching || expandedCategories.has(category.id);
              const hasSelectedComponent = components.some((ct) => ct.type === selectedType);

              return (
                <div key={category.id}>
                  {/* Category header */}
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => !isSearching && toggleCategory(category.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 bg-muted/50 border-b text-sm font-medium transition-colors",
                      isSearching ? "cursor-default" : "hover:bg-muted",
                      hasSelectedComponent && !isExpanded ? "text-primary" : "",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {!isSearching &&
                        (isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        ))}
                      {category.label}
                    </span>
                    <span className="text-xs text-muted-foreground">({components.length})</span>
                  </button>
                  {/* Components in category */}
                  {isExpanded &&
                    components.map((ct) => {
                      const Icon = getComponentIcon(ct.type);
                      const blocked = isBlocked(ct.type);
                      return (
                        <button
                          key={ct.type}
                          type="button"
                          onClick={() => onSelect(ct.type)}
                          className={cn(
                            "w-full text-left pl-3 pr-3 py-2 border-b last:border-b-0 transition-colors flex items-center gap-2",
                            selectedType === ct.type
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted",
                            blocked &&
                              selectedType !== ct.type &&
                              "text-muted-foreground opacity-60",
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <div className="font-medium text-sm">{ct.label}</div>
                        </button>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right: Component description */}
      <div className="p-4 border rounded-lg bg-muted/30">
        {selectedComponentInfo && SelectedIcon ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <SelectedIcon className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-semibold">{selectedComponentInfo.label}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {selectedComponentInfo.detailedDescription}
            </p>
            {(() => {
              // A component restricted to a single page type gets a placement hint, derived
              // from the shared policy (QP-150) rather than stale per-component flags.
              const allowed = allowedPageTypesFor(selectedComponentInfo.type);
              const onlyGoal = allowed.length === 1 && allowed[0] === "goal";
              const onlyGlobal = allowed.length === 1 && allowed[0] === "global";
              const onlyPortfolio = allowed.length === 1 && allowed[0] === "portfolio";
              // QL-212 — for the risk assessment, say which of its four sections
              // this page would actually show, so the designer sees the
              // consequence of the model's scopes before placing it.
              const shownRiskSections =
                selectedComponentInfo.type === "risk_assessment" && riskSections?.size
                  ? RISK_SECTIONS.filter((section: RiskSection) => riskSections.has(section))
                      .map((section: RiskSection) => t[RISK_SECTION_KEYS[section]])
                      .join(", ")
                  : null;
              return (
                (onlyGoal ||
                  onlyGlobal ||
                  onlyPortfolio ||
                  selectedComponentInfo.requiresXRay ||
                  selectedComponentInfo.requiresRiskModel ||
                  selectedComponentInfo.requiresPortfolioCollection ||
                  shownRiskSections ||
                  selectedBlockedReason) && (
                  <div className="pt-2 space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">{t.requirements}</p>
                    <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                      {onlyGoal && <li>{t.goalPagesOnly}</li>}
                      {onlyGlobal && <li>{t.globalPagesOnly}</li>}
                      {onlyPortfolio && <li>{t.portfolioPagesOnly}</li>}
                      {selectedComponentInfo.requiresRiskModel && <li>{t.requiresRiskModel}</li>}
                      {selectedComponentInfo.requiresPortfolioCollection && (
                        <li>{t.requiresPortfolioCollection}</li>
                      )}
                      {selectedComponentInfo.requiresXRay && <li>{t.requiresXRay}</li>}
                      {shownRiskSections && (
                        <li>
                          {t.riskShowsHere} {shownRiskSections}
                        </li>
                      )}
                      {selectedBlockedReason && (
                        <li className="text-destructive">{selectedBlockedReason}</li>
                      )}
                    </ul>
                  </div>
                )
              );
            })()}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t.selectComponentDetails}</p>
        )}
      </div>
    </div>
  );
}
